import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useUpdateOrderStatusMutation } from '../api';
import { sourceLabel } from '../attribution';
import { type UpdateOrderStatusFormValues } from '../schemas';
import type { MerchantDrivenStatus, MerchantOrder, OrderStatus } from '../types';

import { OrderStatusBadge } from './OrderStatusBadge';
import { SendToJibexModal } from './SendToJibexModal';
import { UpdateStatusForm } from './UpdateStatusForm';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { formatCatalogPrice } from '@/features/products/format-price';

const ACTION_LABEL_KEY: Record<MerchantDrivenStatus, MessageKey> = {
  PROCESSING: 'merchant.orders.actionProcessing',
  PACKED: 'merchant.orders.actionPacked',
  CANCELLED: 'merchant.common.cancel',
};

/**
 * The single, deliberately narrow next-status HINT this table offers —
 * a UX affordance, never the authority. `OrderStateMachine`, server-side,
 * remains the only real decision-maker (DEV §9.4): this returns `null`
 * for every status where no merchant-driven transition is currently
 * both legal AND real for a COD order —
 *
 *   - `PROCESSING → CANCELLED` exists in the machine but is guarded
 *     DISABLED pending OQ-05 (SCORE) — offering it would show a button
 *     that always fails; not offered.
 *   - Every other status (PACKED, CANCELLED, or anything not yet
 *     reachable — SHIPPED/DELIVERED/etc., entrée 67) has no
 *     merchant-driven transition defined by DEV §9.4 at all.
 *
 * This is NOT the transition table reimplemented: it names exactly the
 * two rows that are both real and reachable today, nothing more — the
 * API still re-validates and would refuse anything this function got
 * wrong.
 */
function nextMerchantStatus(status: OrderStatus): MerchantDrivenStatus | null {
  if (status === 'PAYMENT_CONFIRMED') return 'PROCESSING';
  if (status === 'PROCESSING') return 'PACKED';
  return null;
}

/**
 * The same two statuses `nextMerchantStatus` above offers a real action
 * for — exported so the Dashboard's "commandes à traiter" alert
 * (`features/dashboard/api.ts`) counts exactly what this table itself
 * would let the merchant act on, never a separately-invented definition
 * of "needs attention".
 */
export const ACTIONABLE_ORDER_STATUSES: readonly OrderStatus[] = [
  'PAYMENT_CONFIRMED',
  'PROCESSING',
];

function reportError(
  error: unknown,
  toast: ReturnType<typeof useToast>,
  t: ReturnType<typeof useI18n>['t'],
): void {
  toast.error(t(resolveErrorMessageKey(error)));
}

export function OrderTable({ orders }: { orders: readonly MerchantOrder[] }): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const updateStatus = useUpdateOrderStatusMutation();
  const [editing, setEditing] = useState<{ id: string; target: MerchantDrivenStatus } | null>(null);
  // « Confirmer et envoyer à Jibex »: the order a merchant reviews before it becomes « Préparée » and is sent.
  const [sending, setSending] = useState<string | null>(null);

  async function markPackedWithoutCarrier(): Promise<void> {
    if (sending === null) return;
    try {
      await updateStatus.mutateAsync({ orderId: sending, status: 'PACKED' });
      toast.success(t('merchant.orders.statusUpdated'));
      setSending(null);
    } catch (error) {
      reportError(error, toast, t);
    }
  }

  async function onSubmit(values: UpdateOrderStatusFormValues): Promise<void> {
    if (editing === null) return;
    try {
      await updateStatus.mutateAsync({
        orderId: editing.id,
        status: values.status,
        ...(values.reason !== undefined ? { reason: values.reason } : {}),
      });
      toast.success(t('merchant.orders.statusUpdated'));
      setEditing(null);
    } catch (error) {
      reportError(error, toast, t);
    }
  }

  return (
    <>
      <Table caption={t('merchant.orders.title')}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('merchant.orders.table.colOrder')}</TableHeaderCell>
            <TableHeaderCell>{t('merchant.orders.table.colCustomer')}</TableHeaderCell>
            <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
            <TableHeaderCell>{t('merchant.orders.table.colTotal')}</TableHeaderCell>
            <TableHeaderCell>{t('merchant.orders.table.colDate')}</TableHeaderCell>
            <TableHeaderCell> </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => {
            const next = nextMerchantStatus(order.status);
            return (
              <TableRow key={order.id}>
                <TableCell>
                  <span className="font-mono text-sm text-ink">#{order.orderNumber}</span>
                  {order.source !== 'online' ? (
                    <span className="ms-2 rounded-full bg-accentSoft px-2 py-0.5 text-[11px] font-medium text-accent">
                      {t(
                        order.source === 'manual_sale'
                          ? 'merchant.orders.sourceSale'
                          : 'merchant.orders.sourceManual',
                      )}
                    </span>
                  ) : null}
                  {order.attribution !== null && order.attribution.source !== 'direct' ? (
                    <span
                      className="ms-2 rounded-full bg-ground px-2 py-0.5 text-[11px] font-medium text-inkSoft"
                      title={order.attribution.campaign ?? undefined}
                    >
                      {t('merchant.attribution.via', {
                        source: sourceLabel(order.attribution.source, t),
                      })}
                    </span>
                  ) : null}
                  <p className="text-xs text-inkFaint">
                    {t('merchant.orders.itemCount', {
                      count: order.items.length,
                      plural: order.items.length > 1 ? 's' : '',
                    })}
                  </p>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-ink">{order.customerName}</span>
                  <p className="text-xs text-inkFaint">{order.customerPhone}</p>
                </TableCell>
                <TableCell>
                  <OrderStatusBadge status={order.status} />
                </TableCell>
                <TableCell>
                  <span className="font-mono text-sm text-ink">
                    {formatCatalogPrice(order.grandTotal)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-inkFaint">
                    {new Date(order.createdAt).toLocaleString('fr-TN')}
                  </span>
                </TableCell>
                <TableCell align="end">
                  {next === 'PACKED' ? (
                    // The order is sent to Jibex from a review of the parcel, not a bare status change.
                    <Button
                      size="sm"
                      onClick={() => {
                        setSending(order.id);
                      }}
                    >
                      {t('merchant.sendJibex.action')}
                    </Button>
                  ) : next !== null ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditing({ id: order.id, target: next });
                      }}
                    >
                      {t(ACTION_LABEL_KEY[next])}
                    </Button>
                  ) : order.status === 'PACKED' ? (
                    // Already « Préparée »: see the parcel, or correct and re-send a send Jibex refused.
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setSending(order.id);
                      }}
                    >
                      {t('merchant.sendJibex.action')}
                    </Button>
                  ) : (
                    <span className="text-xs text-inkFaint">
                      {order.status === 'CANCELLED' ||
                      order.status === 'DELIVERED' ||
                      order.status === 'REFUNDED'
                        ? t('merchant.orders.completed')
                        : t('merchant.orders.noActionNeeded')}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Modal
        open={editing !== null}
        title={t('merchant.orders.updateModalTitle')}
        onClose={() => {
          setEditing(null);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(null);
              }}
            >
              {t('merchant.common.cancel')}
            </Button>
            <Button type="submit" form="update-order-status-form" loading={updateStatus.isPending}>
              {t('merchant.common.confirm')}
            </Button>
          </>
        }
      >
        {editing !== null ? (
          <UpdateStatusForm
            targetStatus={editing.target}
            onSubmit={(values) => {
              void onSubmit(values);
            }}
            isSubmitting={updateStatus.isPending}
          />
        ) : null}
      </Modal>

      <SendToJibexModal
        orderId={sending}
        onClose={() => {
          setSending(null);
        }}
        onMarkPacked={() => {
          void markPackedWithoutCarrier();
        }}
        markingPacked={updateStatus.isPending}
      />
    </>
  );
}
